import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Hash, Mail } from 'lucide-react';
import PageLayout from '../components/PageLayout';

const LAST_UPDATED = 'May 1, 2026';

const SECTIONS = [
  { id: 'overview',     label: '1. Overview' },
  { id: 'data-collect', label: '2. Data We Collect' },
  { id: 'data-use',     label: '3. How We Use Data' },
  { id: 'data-share',   label: '4. Data Sharing' },
  { id: 'cookies',      label: '5. Cookies' },
  { id: 'retention',    label: '6. Data Retention' },
  { id: 'rights',       label: '7. Your Rights' },
  { id: 'security',     label: '8. Security' },
  { id: 'children',     label: "9. Children's Privacy" },
  { id: 'changes',      label: '10. Changes to Policy' },
  { id: 'contact',      label: '11. Contact Us' },
];

function H2({ id, children }) {
  return (
    <h2 id={id} className="text-xl font-bold text-white mt-12 mb-4 flex items-center gap-2 scroll-mt-24 group">
      {children}
      <a href={`#${id}`} className="opacity-0 group-hover:opacity-40 transition-opacity">
        <Hash size={14} className="text-slate-500" />
      </a>
    </h2>
  );
}

function P({ children }) {
  return <p className="text-slate-400 text-sm leading-relaxed mb-4">{children}</p>;
}

function UL({ items }) {
  return (
    <ul className="space-y-2 mb-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#00E5FF]/50 flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicy() {
  const [activeId, setActiveId] = useState('overview');

  useEffect(() => {
    const ids = SECTIONS.map(s => s.id);
    const obs = new IntersectionObserver(
      entries => { const v = entries.filter(e => e.isIntersecting); if (v.length) setActiveId(v[0].target.id); },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    ids.forEach(id => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PageLayout
      badge="Legal"
      badgeColor="purple"
      title="Privacy Policy"
      subtitle={`Last updated: ${LAST_UPDATED}`}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex gap-14">

          {/* Sticky TOC */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24 glass-card border border-white/[0.06] p-4 rounded-2xl">
              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-3">Contents</p>
              <nav className="space-y-0.5">
                {SECTIONS.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => scrollTo(id)}
                    className={[
                      'w-full text-left px-2.5 py-2 rounded-lg text-xs transition-all',
                      activeId === id
                        ? 'bg-[#7C3AED]/10 text-[#7C3AED] font-semibold'
                        : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.03]',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-3xl">

            <div className="glass-card border border-white/[0.06] p-4 rounded-xl mb-8 flex items-start gap-3">
              <Shield size={15} className="text-[#7C3AED] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-400 leading-relaxed">
                At ZenithCipher, we are committed to protecting your personal information and your right to privacy.
                This policy explains what data we collect, how we use it, and what rights you have over it.
              </p>
            </div>

            <section id="overview">
              <H2 id="overview">1. Overview</H2>
              <P>ZenithCipher, Inc. ("ZenithCipher", "we", "us", or "our") operates the website zenithcipher.io and
                 the ZenithCipher Web3 security intelligence platform (the "Service"). This Privacy Policy applies to
                 all personal information collected through our Service.</P>
              <P>By using our Service, you agree to the collection and use of information in accordance with this policy.
                 If you do not agree with this policy, please do not use our Service.</P>
            </section>

            <section id="data-collect">
              <H2 id="data-collect">2. Data We Collect</H2>
              <P>We collect information you provide directly and information generated through your use of the Service:</P>
              <p className="text-sm font-semibold text-white mb-2">Account Information</p>
              <UL items={[
                'Email address (required for account creation)',
                'Password (stored as a bcrypt hash — we never store plaintext passwords)',
                'Name (optional, for account display purposes)',
                'Google account information (if you sign in with Google: name, email, profile photo)',
              ]} />
              <p className="text-sm font-semibold text-white mb-2">Usage Data</p>
              <UL items={[
                'Wallet addresses submitted for scanning (public blockchain data)',
                'Scan results and risk reports associated with your account',
                'API key activity and usage statistics',
                'Log data: IP address, browser type, pages visited, time and date of visits',
              ]} />
              <p className="text-sm font-semibold text-white mb-2">Payment Data</p>
              <P>If you upgrade to a paid plan, payment is processed by Stripe. We do not store credit card numbers or
                 payment instrument details. We receive a Stripe customer ID and subscription status.</P>
            </section>

            <section id="data-use">
              <H2 id="data-use">3. How We Use Data</H2>
              <P>We use collected data to:</P>
              <UL items={[
                'Provide, maintain, and improve the Service',
                'Authenticate your identity and manage your account',
                'Generate and store wallet risk scan reports',
                'Send transactional emails (account confirmation, password reset)',
                'Send product updates and security advisories (you may opt out)',
                'Enforce our Terms of Service and prevent abuse',
                'Analyse aggregate usage patterns to improve the platform',
                'Comply with legal obligations',
              ]} />
              <P>We do not use your data for advertising. We do not build behavioural profiles for third-party ad targeting.</P>
            </section>

            <section id="data-share">
              <H2 id="data-share">4. Data Sharing</H2>
              <P>We do not sell your personal information. We share data only in these limited circumstances:</P>
              <UL items={[
                'Service providers: Stripe (payments), MongoDB Atlas (database hosting), AWS (cloud infrastructure). These providers act as data processors under our direction.',
                'Legal compliance: when required by law, court order, or governmental authority.',
                'Business transfers: in the event of a merger, acquisition, or asset sale, user data may be transferred. We will notify you via email before this occurs.',
                'With your consent: in any other case, only with your explicit permission.',
              ]} />
            </section>

            <section id="cookies">
              <H2 id="cookies">5. Cookies</H2>
              <P>We use essential cookies to maintain your authentication session. We do not use tracking or
                 advertising cookies. See our <Link to="/cookies" className="text-[#00E5FF] hover:underline">Cookie Policy</Link> for full details.</P>
            </section>

            <section id="retention">
              <H2 id="retention">6. Data Retention</H2>
              <P>We retain your account data for as long as your account is active. Scan reports are retained
                 according to your plan (7 days for Free, 30 days for Starter, unlimited for Pro and Enterprise).
                 Log data is retained for 90 days. When you delete your account, all associated personal data is
                 deleted within 30 days.</P>
            </section>

            <section id="rights">
              <H2 id="rights">7. Your Rights</H2>
              <P>Depending on your jurisdiction, you may have the following rights:</P>
              <UL items={[
                'Access: request a copy of the personal data we hold about you',
                'Correction: request correction of inaccurate or incomplete data',
                'Deletion: request deletion of your personal data ("right to be forgotten")',
                'Portability: receive your data in a structured, machine-readable format',
                'Objection: object to processing of your data for certain purposes',
                'Restriction: request restriction of processing in certain circumstances',
              ]} />
              <P>To exercise any of these rights, contact us at <a href="mailto:privacy@zenithcipher.io" className="text-[#00E5FF] hover:underline">privacy@zenithcipher.io</a>. We will respond within 30 days.</P>
            </section>

            <section id="security">
              <H2 id="security">8. Security</H2>
              <P>We implement industry-standard security measures including TLS encryption in transit, bcrypt password
                 hashing, JWT token authentication with expiry, and rate limiting. We conduct regular security reviews.
                 No method of transmission over the internet is 100% secure — see our{' '}
                 <Link to="/security" className="text-[#00E5FF] hover:underline">Security Policy</Link> for full details.</P>
            </section>

            <section id="children">
              <H2 id="children">9. Children's Privacy</H2>
              <P>Our Service is not directed to individuals under the age of 16. We do not knowingly collect personal
                 information from children. If you believe we have inadvertently collected data from a minor, please
                 contact us immediately at <a href="mailto:privacy@zenithcipher.io" className="text-[#00E5FF] hover:underline">privacy@zenithcipher.io</a>.</P>
            </section>

            <section id="changes">
              <H2 id="changes">10. Changes to This Policy</H2>
              <P>We may update this Privacy Policy from time to time. When we make significant changes, we will notify
                 you by email and update the "Last updated" date at the top of this page. Continued use of the Service
                 after changes constitutes acceptance of the updated policy.</P>
            </section>

            <section id="contact">
              <H2 id="contact">11. Contact Us</H2>
              <P>For privacy-related questions or requests:</P>
              <div className="glass-card border border-white/[0.06] p-5 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Mail size={14} className="text-[#00E5FF]" />
                  <span className="text-sm font-semibold text-white">Privacy Team</span>
                </div>
                <p className="text-sm text-slate-400 mb-1">Email: <a href="mailto:privacy@zenithcipher.io" className="text-[#00E5FF] hover:underline">privacy@zenithcipher.io</a></p>
                <p className="text-sm text-slate-400 mb-1">Address: ZenithCipher, Inc., 340 Pine Street, Suite 800, San Francisco, CA 94104</p>
                <p className="text-xs text-slate-600 mt-3">Response time: within 30 calendar days</p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </PageLayout>
  );
}
